import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';
import { ChartDataPoint } from '../../types';
import Card from '../common/Card';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface BarChartProps {
  title: string;
  subtitle?: string;
  data: ChartDataPoint[];
  height?: number;
  horizontal?: boolean;
  showLegend?: boolean;
}

const BarChart: React.FC<BarChartProps> = ({
  title,
  subtitle,
  data,
  height = 300,
  horizontal = false,
  showLegend = false,
}) => {
  const isDark = document.documentElement.classList.contains('dark');

  // Generate gradient colors for each bar
  const colors = [
    '#0ea5e9', // Primary blue
    '#06b6d4', // Cyan
    '#8b5cf6', // Purple
    '#ec4899', // Pink
    '#f59e0b', // Amber
  ];

  const chartData = {
    labels: data.map(d => d.label),
    datasets: [
      {
        label: title,
        data: data.map(d => d.value),
        backgroundColor: data.map((_, index) => `${colors[index % colors.length]}99`),
        borderColor: data.map((_, index) => colors[index % colors.length]),
        borderWidth: 2,
        borderRadius: 8,
        barPercentage: 0.7,
        categoryPercentage: 0.8,
      },
    ],
  };

  const options: ChartOptions<'bar'> = {
    indexAxis: horizontal ? 'y' : 'x',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: showLegend,
        position: 'top' as const,
        labels: {
          color: isDark ? '#cbd5e1' : '#475569',
          font: {
            size: 12,
            weight: 500,
          },
          padding: 15,
          usePointStyle: true,
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: isDark ? '#1e293b' : '#ffffff',
        titleColor: isDark ? '#f1f5f9' : '#0f172a',
        bodyColor: isDark ? '#cbd5e1' : '#475569',
        borderColor: isDark ? '#334155' : '#e2e8f0',
        borderWidth: 1,
        padding: 12,
        bodyFont: {
          size: 13,
        },
        titleFont: {
          size: 14,
          weight: 'bold',
        },
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              const value = horizontal ? context.parsed.x : context.parsed.y;
              if (typeof value === 'number') {
                label += new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                }).format(value);
              }
            }
            return label;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: isDark ? '#94a3b8' : '#64748b',
          font: {
            size: 11,
          },
        },
        border: {
          display: false,
        },
      },
      y: {
        grid: {
          color: isDark ? '#1e293b' : '#f1f5f9',
          lineWidth: 1,
        },
        ticks: {
          color: isDark ? '#94a3b8' : '#64748b',
          font: {
            size: 11,
          },
          callback: function(value) {
            if (horizontal) {
              return value;
            }
            return new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(value as number);
          },
        },
        border: {
          display: false,
        },
      },
    },
  };

  return (
    <Card title={title} subtitle={subtitle} hover>
      <div style={{ height: `${height}px` }} className="chart-container">
        <Bar data={chartData} options={options} />
      </div>
    </Card>
  );
};

export default BarChart;
